import React from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTrigger,
  Group,
  Heading,
  Input,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  SearchField,
  VisuallyHidden,
} from 'react-aria-components';

import { iconCheck } from '../../icons/glyphs/check';
import { iconChevronDown } from '../../icons/glyphs/chevron-down';
import { iconClose } from '../../icons/glyphs/close';
import { Icon } from '../../icons/icon';

import type { ComboboxOption, ComboboxProps } from './combobox';
import { ComboboxSpinner } from './combobox-spinner';

import styles from './combobox.module.css';
import trayStyles from './combobox-tray.module.css';

/** Compares typed text with a label: case and edge whitespace do not count. */
function normalize(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * The tray presentation: a button on the page that opens the search and the
 * list in a modal over the whole visible viewport.
 *
 * @remarks
 * The field on the page is a button, not an input, so tapping it raises no
 * on-screen keyboard until the search box inside the tray has focus. The tray
 * makes no commit decision of its own: it forwards `onKeyDown` exactly like the
 * popover, and closes when the consumer's `selectedKey` changes.
 */
export function ComboboxTray(
  props: ComboboxProps & {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
  },
): React.ReactElement {
  const {
    allowsCustomValue = false,
    description,
    emptyText = 'No results found.',
    hint,
    inputValue,
    isDisabled = false,
    isInvalid = false,
    isLoading = false,
    isOpen,
    isRequired = false,
    label,
    onInputChange,
    onKeyDown,
    onOpenChange,
    onSelectionChange,
    options,
    placeholder,
    selectedKey,
  } = props;

  const id = React.useId();
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const requiredId = `${id}-required`;
  const descriptionId = `${id}-description`;
  const headingId = `${id}-heading`;
  const name = label ?? placeholder ?? 'Select an option';

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const openedInputValueRef = React.useRef(inputValue);
  const openedKeyRef = React.useRef(selectedKey);

  const open = () => {
    openedInputValueRef.current = inputValue;
    openedKeyRef.current = selectedKey;
    // Open first: the parent then freezes the presentation before the
    // on-screen keyboard can resize the viewport.
    onOpenChange(true);
    onInputChange('');
  };

  const close = React.useCallback(
    (committed: boolean) => {
      onOpenChange(false);
      if (!committed) onInputChange(openedInputValueRef.current);
    },
    [onInputChange, onOpenChange],
  );

  // One close signal owned by the consumer: its selection changed. That covers
  // a list pick and a typed value committed from the consumer's onKeyDown.
  React.useEffect(() => {
    if (isOpen && selectedKey !== openedKeyRef.current) close(true);
  }, [close, isOpen, selectedKey]);

  // Enter on the value that is already selected changes no key, so the close
  // signal above never fires. The consumer still decides every commit: this
  // only closes when the typed text already is the current value, and restores
  // the label as it was so "alpha " does not stay behind in the field.
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    onKeyDown?.(event);
    if (event.key !== 'Enter' || selectedKey === null) return;
    const current = options.find(option => option.value === selectedKey);
    if (current === undefined) return;
    if (normalize(inputValue) === normalize(current.label)) close(false);
  };

  // Clearing inside the tray is a choice, not a dismissal: the tray stays open,
  // so the key it compares against moves along, and Cancel afterwards keeps
  // the field empty instead of restoring the old label.
  const clearInTray = () => {
    openedKeyRef.current = null;
    openedInputValueRef.current = '';
    onSelectionChange(null);
    searchRef.current?.focus();
  };

  const selectedLabel =
    selectedKey === null
      ? undefined
      : options.find(option => option.value === selectedKey)?.label ??
        (inputValue || undefined);

  // While searching, the selected option may be filtered out of `options`; the
  // label the field showed when the tray opened still names it.
  const currentChoiceLabel =
    selectedKey === null
      ? undefined
      : options.find(option => option.value === selectedKey)?.label ??
        openedInputValueRef.current;

  const labelledBy = [
    label === undefined ? undefined : labelId,
    label === undefined ? undefined : valueId,
    isRequired ? requiredId : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={styles['ns-combobox']}
      data-disabled={isDisabled || undefined}
      data-invalid={isInvalid || undefined}
    >
      {label !== undefined && (
        <span className={styles['ns-combobox__label']} id={labelId}>
          {label}
          {isRequired && (
            <span
              aria-hidden
              className={styles['ns-combobox__secondary-label']}
            >
              *
            </span>
          )}
        </span>
      )}
      {isRequired && <VisuallyHidden id={requiredId}>required</VisuallyHidden>}

      <Group
        className={`${styles['ns-combobox__input']} ${trayStyles['ns-combobox__tray-field']}`}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
      >
        <DialogTrigger
          isOpen={isOpen}
          onOpenChange={next => (next ? open() : close(false))}
        >
          <Button
            ref={triggerRef}
            aria-describedby={
              description === undefined ? undefined : descriptionId
            }
            aria-label={label === undefined ? name : undefined}
            aria-labelledby={labelledBy === '' ? undefined : labelledBy}
            className={trayStyles['ns-combobox__trigger']}
            isDisabled={isDisabled}
          >
            <span
              className={
                selectedLabel === undefined
                  ? trayStyles['ns-combobox__trigger-value--placeholder']
                  : undefined
              }
              id={valueId}
            >
              {selectedLabel ?? placeholder ?? 'Select an option'}
            </span>
          </Button>

          <ModalOverlay
            isDismissable
            className={trayStyles['ns-combobox__tray-overlay']}
          >
            <Modal className={trayStyles['ns-combobox__tray']}>
              <Dialog
                aria-labelledby={headingId}
                className={trayStyles['ns-combobox__tray-dialog']}
              >
                <div className={trayStyles['ns-combobox__tray-header']}>
                  <Heading
                    className={`${styles['ns-combobox__label']} ${trayStyles['ns-combobox__tray-title']}`}
                    id={headingId}
                    slot="title"
                  >
                    {name}
                  </Heading>
                  {/* Text, not an icon: on a touch screen there is no Escape,
                      and this has to read as the way out without choosing. */}
                  <Button
                    className={trayStyles['ns-combobox__tray-cancel']}
                    slot="close"
                  >
                    Cancel
                  </Button>
                </div>

                <Autocomplete
                  // With a creatable source, no option is focused while
                  // typing, so Enter reaches the consumer's onKeyDown.
                  disableAutoFocusFirst={allowsCustomValue}
                  inputValue={inputValue}
                  onInputChange={onInputChange}
                >
                  <SearchField
                    autoFocus
                    aria-label={name}
                    className={trayStyles['ns-combobox__tray-search']}
                  >
                    <Group className={styles['ns-combobox__input']}>
                      <Input
                        ref={searchRef}
                        className={styles['ns-combobox__input-field']}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={placeholder}
                      />
                      {isLoading && (
                        <ComboboxSpinner
                          className={`${styles['ns-combobox__spinner']} ${trayStyles['ns-combobox__tray-search-spinner']}`}
                        />
                      )}
                    </Group>
                  </SearchField>
                  {currentChoiceLabel !== undefined && (
                    <div className={trayStyles['ns-combobox__tray-current']}>
                      <span
                        className={
                          trayStyles['ns-combobox__tray-current-label']
                        }
                      >
                        Current choice: {currentChoiceLabel}
                      </span>
                      <Button
                        aria-label="Clear selection"
                        className={`${styles['ns-combobox__input-clear']} ${trayStyles['ns-combobox__tray-current-clear']}`}
                        onPress={clearInTray}
                      >
                        <Icon glyph={iconClose} size={16} />
                      </Button>
                    </div>
                  )}
                  <ListBox
                    aria-label={name}
                    className={`${styles['ns-combobox__list']} ${trayStyles['ns-combobox__tray-list']}`}
                    items={options}
                    // Selection, not onAction: react-aria fires onAction only
                    // while the selection is empty, so a second pick would be
                    // lost.
                    onSelectionChange={keys => {
                      if (keys === 'all') return;
                      const [key] = [...keys];
                      if (key === undefined) {
                        // The current value was tapped again: keep it.
                        close(false);
                        return;
                      }
                      onSelectionChange(String(key));
                    }}
                    renderEmptyState={() => (
                      <div className={styles['ns-combobox__no-results']}>
                        {emptyText}
                      </div>
                    )}
                    selectedKeys={selectedKey === null ? [] : [selectedKey]}
                    selectionMode="single"
                  >
                    {(item: ComboboxOption) => (
                      <ListBoxItem
                        className={styles['ns-combobox__item']}
                        id={item.value}
                        isDisabled={item.disabled}
                        textValue={item.label}
                      >
                        {({ isSelected }) => (
                          <>
                            <span className={styles['ns-combobox__item-label']}>
                              {item.label}
                            </span>
                            {isSelected && (
                              <Icon
                                className={
                                  trayStyles['ns-combobox__tray-check']
                                }
                                glyph={iconCheck}
                                size={16}
                              />
                            )}
                          </>
                        )}
                      </ListBoxItem>
                    )}
                  </ListBox>
                </Autocomplete>

                {hint !== undefined && (
                  <div className={styles['ns-combobox__hint']} role="note">
                    {hint}
                  </div>
                )}
              </Dialog>
            </Modal>
          </ModalOverlay>
        </DialogTrigger>

        {selectedKey !== null && (
          <Button
            aria-label="Clear selection"
            className={`${styles['ns-combobox__input-clear']} ${trayStyles['ns-combobox__tray-clear']}`}
            isDisabled={isDisabled}
            onPress={() => {
              onSelectionChange(null);
              triggerRef.current?.focus();
            }}
          >
            <Icon glyph={iconClose} size={16} />
          </Button>
        )}
        {isLoading && (
          <ComboboxSpinner className={styles['ns-combobox__spinner']} />
        )}
        <span aria-hidden className={styles['ns-combobox__input-chevron']}>
          <Icon glyph={iconChevronDown} size={16} />
        </span>
      </Group>

      {description !== undefined && (
        <span className={styles['ns-combobox__description']} id={descriptionId}>
          {description}
        </span>
      )}
    </div>
  );
}
